import { useCallback, useEffect, useState } from 'react';

/**
 * The desktop bridge, as the web app sees it.
 *
 * `window.bemedic` is injected by the Electron preload and exists nowhere else, so its
 * presence is the capability check: the same bundle renders session controls in the
 * desktop app and an explanation in a browser tab. The alternative — a disabled button
 * on the web — advertises a capability the client does not have, which is worse than
 * saying so.
 *
 * The state is PUSHED from the main process, which owns the session, the heartbeat and
 * the idle watch. Nothing here polls, and nothing here is authoritative.
 */

export interface DesktopSession {
  id: string;
  status: 'active' | 'paused' | 'stopped' | 'stale';
  startedAt: string;
  totalMs: number;
  activeMs: number;
  idleMs: number;
  pausedMs: number;
}

export interface DesktopState {
  session: DesktopSession | null;
  idle: boolean;
  online: boolean;
  queued: number;
  version: string;
  platform: string;
  preferences: {
    autoStartOnLogin: boolean;
    idleThresholdMinutes: number;
    pauseOnIdle: boolean;
  };
}

interface DesktopApi {
  isDesktop: true;
  getState(): Promise<DesktopState>;
  startSession(): Promise<DesktopState>;
  pauseSession(): Promise<DesktopState>;
  resumeSession(): Promise<DesktopState>;
  stopSession(): Promise<DesktopState>;
  setPreference(key: string, value: boolean | number): Promise<DesktopState>;
  openExternal(url: string): Promise<void>;
  notify(title: string, body: string, redirectUrl?: string | null): Promise<void>;
  signedIn(): Promise<void>;
  signedOut(): Promise<void>;
  onStateChanged(listener: (state: DesktopState) => void): () => void;
  onNavigate(listener: (path: string) => void): () => void;
}

declare global {
  interface Window {
    bemedic?: DesktopApi;
  }
}

export function desktopApi(): DesktopApi | null {
  return typeof window !== 'undefined' && window.bemedic ? window.bemedic : null;
}

export function useDesktop() {
  const api = desktopApi();
  const [state, setState] = useState<DesktopState | null>(null);

  useEffect(() => {
    if (!api) return;

    let cancelled = false;
    void api.getState().then((initial) => {
      if (!cancelled) setState(initial);
    });

    const unsubscribe = api.onStateChanged(setState);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [api]);

  const start = useCallback(async () => setState(await api?.startSession() ?? null), [api]);
  const pause = useCallback(async () => setState(await api?.pauseSession() ?? null), [api]);
  const resume = useCallback(async () => setState(await api?.resumeSession() ?? null), [api]);
  const stop = useCallback(async () => setState(await api?.stopSession() ?? null), [api]);

  return { isDesktop: api !== null, state, start, pause, resume, stop };
}
